import { useMemo, useState, useEffect } from "react";
import TableUnidades from '../Components/TableUnidades';
import Api from '../Auth/Api';
import { useForm } from "react-hook-form";
import { Modal, Toast } from 'react-bootstrap';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import Select from 'react-select';
import { decodeJWT } from "../Components/jwtUtils";

function Unidades() {
    const token = JSON.parse(localStorage.getItem("user_token"));
    const nivel = decodeJWT(token).nivel;

    const LoadingScreen = () => {
        return (
            <div className="d-flex align-items-center justify-content-center p-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        )
    }

    const [showModalEditarUnidade, setShowModalEditarUnidade] = useState(false);
    const [showModalDeletarUnidade, setShowModalDeletarUnidade] = useState({ show: false, id: null, nome: '' });
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingSave, setLoadingSave] = useState(false);

    const { register, handleSubmit, setValue, formState: { errors } } = useForm();

    const [dataUnidades, setDataUnidades] = useState([]);
    const [dataEmpresas, setDataEmpresas] = useState([]);
    const [selectedEmpresa, setSelectedEmpresa] = useState(null);

    const customStyles = {
        container: (base) => ({
            ...base,
            width: '100%',
        }),
        control: (base, state) => ({
            ...base,
            fontSize: "1rem",
            backgroundColor: "#ffffff",
            borderRadius: "0.25rem",
            boxShadow: state.isFocused ? "0 0 0 0.25rem #27485b73" : null,
            borderColor: "#dee2e6",
            "&:hover": {
                borderColor: "#dee2e6"
            },
        }),
        singleValue: (base) => ({
            ...base,
            color: '#000',
            marginTop: '5px',
        }),
        input: (base) => ({
            ...base,
            color: '#adb5bd',
            height: "46px",
            margin: '0px',
            padding: '0px !important',

        }),
        menu: (base) => ({
            ...base,
            backgroundColor: '#fff',
        }),
        option: (base, state) => ({
            ...base,
            color: state.isSelected ? '#3a5a6c' : null,
            backgroundColor: state.isFocused ? '#27485b73' : null,
            "&:active": {
                backgroundColor: "#27485b73"
            },
        }),
    };

    useEffect(() => {
        setLoading(true);
        async function fetchData() {
            try {
                const response = await Api.get(`api/listar-unidades`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataUnidades(response.data);
                setLoading(false);
            } catch (error) {
                console.error(error.response?.data?.message || 'Erro ao carregar unidades');
                setLoading(false);
                setDataUnidades([]);
            }
        }
        fetchData();
    }, [token, toast]);

    // Buscar empresas para seleção (apenas para administradores)
    useEffect(() => {
        if (nivel === 'Administrador') {
            async function fetchEmpresas() {
                try {
                    const response = await Api.get(`api/listar-empresas`, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    setDataEmpresas(response.data);
                } catch (error) {
                    console.error('Erro ao carregar empresas:', error);
                    setDataEmpresas([]);
                }
            }
            fetchEmpresas();
        }
    }, [token, nivel]);

    const onSubmitEditarUnidade = async (data) => {
        setLoadingSave(true);
        
        // Validar se administrador selecionou uma empresa
        if (nivel === 'Administrador' && !selectedEmpresa) {
            setToast({ header: 'Ops!', body: 'Por favor, selecione uma empresa.' });
            setShowToast(true);
            setLoadingSave(false);
            return;
        }
        
        // Adicionar id_empresa se for administrador e empresa foi selecionada
        if (nivel === 'Administrador' && selectedEmpresa) {
            data.id_empresa = selectedEmpresa.value;
        }
        
        try {
            const response = await Api.post(`api/editar-unidade/${data.id}`, data, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setToast({ header: 'Sucesso!', body: response.data.message });
        } catch (error) {
            setToast({ header: 'Ops!', body: error.response?.data?.message || 'Erro ao editar unidade' });
            console.error("Erro ao editar unidade:", error);
        }
        setLoadingSave(false);
        setShowToast(true);
        setShowModalEditarUnidade(false);
        setSelectedEmpresa(null);
    }

    const handleDeletarUnidade = async (id) => {
        try {
            const response = await Api.delete(`api/deletar-unidade/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setToast({ header: 'Sucesso!', body: response.data.message });
        } catch (error) {
            setToast({ header: 'Ops!', body: error.response?.data?.message || 'Erro ao deletar unidade' });
            console.error("Erro ao deletar unidade:", error);
        }
        setShowModalDeletarUnidade({ show: false, id: null, nome: '' });
        setShowToast(true);
    };

    const columns = useMemo(
        () => [
            {
                Header: '',
                accessor: 'id',
                Cell: ({ value }) => (
                    <span className="fw-bold text-primary">{value}</span>
                )
            },
            {
                Header: 'Nome',
                accessor: 'nome',
                Cell: ({ value }) => (
                    <span className="fw-medium">{value}</span>
                )
            },
            {
                Header: 'Empresa',
                accessor: 'empresa_nome',
                Cell: ({ value }) => (
                    <span className="text-muted">{value || 'N/A'}</span>
                )
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ value }) => (
                    <span className={`badge ${value === 1 ? 'bg-success' : 'bg-danger'}`}>
                        {value === 1 ? 'Ativo' : 'Inativo'}
                    </span>
                )
            },
            {
                Header: 'Ações',
                accessor: 'acoes',
                Cell: ({ row: { original } }) => (
                    <div className="text-center">
                        {/* Só mostra botões de editar/deletar para Administrador e Moderador */}
                        {(nivel === 'Administrador' || nivel === 'Moderador') && (
                            <>
                                <Tippy content="Editar unidade">
                                    <button
                                        onClick={() => {
                                            setValue('id', original.id);
                                            setValue('nome', original.nome);
                                            
                                            // Se for administrador e há empresa vinculada
                                            if (nivel === 'Administrador' && original.id_empresa) {
                                                const empresaAtual = dataEmpresas.find(e => e.id === original.id_empresa);
                                                if (empresaAtual) {
                                                    setSelectedEmpresa({
                                                        value: empresaAtual.id,
                                                        label: empresaAtual.titulo
                                                    });
                                                }
                                            }
                                            
                                            setShowModalEditarUnidade(true);
                                        }}
                                        className="btn btn-outline-primary justify-content-center align-items-center rounded px-2 me-2 btn_editar"
                                    >
                                        <i className="bi bi-pencil" />
                                    </button>
                                </Tippy>

                                <Tippy content="Deletar unidade">
                                    <button
                                        onClick={() => setShowModalDeletarUnidade({ show: true, id: original.id, nome: original.nome })}
                                        className="btn btn-outline-primary justify-content-center align-items-center rounded px-2"
                                    >
                                        <i className="bi bi-trash" />
                                    </button>
                                </Tippy>
                            </>
                        )}
                    </div>
                )
            }
        ],
        [dataEmpresas, nivel, setValue]
    );

    if (loading) {
        return <LoadingScreen />;
    }

    return (
        <>
            <Toast show={showToast} onClose={() => setShowToast(false)} delay={3000} autohide style={{ position: 'fixed', left: '95%', bottom: '10px', transform: 'translateX(-95%)', zIndex: 9999, padding: 0 }}>
                <Toast.Header>
                    <strong className="mr-auto">{toast.header}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>

            <div className="fade-in w-100">
                <span className="text-primary fs-1">Lista de Unidades</span>
                <p className="fs-6">Consulte as unidades cadastradas, adicione novas, edite e exclua.</p>

                <div className={loading ? "" : "d-none"}>
                    <LoadingScreen />
                </div>
                <div className={loading ? "d-none" : ""}>
                    <TableUnidades 
                        columns={columns} 
                        data={dataUnidades} 
                        titulo="Lista de Unidades" 
                    />
                </div>
            </div>

            {/* Modal Editar Unidade */}
            <Modal size="lg" show={showModalEditarUnidade} onHide={() => {
                setShowModalEditarUnidade(false);
                setSelectedEmpresa(null);
            }}>
                <form onSubmit={handleSubmit(onSubmitEditarUnidade)}>
                    <Modal.Header closeButton>
                        <span className="fw-semibold fs-4 text-primary">Editar unidade</span>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="container">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="body">
                                        <div className="d-flex">
                                            <div className="d-flex flex-column w-100">
                                                <input type="hidden" {...register("id")} />
                                                
                                                <div className="row mb-3">
                                                    <div className="col-12">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Nome da Unidade</label>
                                                            <input 
                                                                type="text" 
                                                                {...register("nome", { required: true })} 
                                                                placeholder="Nome da unidade" 
                                                                className="form-control border-primary"
                                                            />
                                                            {errors.nome && <span className='text-danger fs-7'>Nome da unidade é obrigatório.</span>}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Campo empresa - apenas para administradores */}
                                                {nivel === 'Administrador' && (
                                                    <div className="row mb-3">
                                                        <div className="col-12">
                                                            <div className="mb-3">
                                                                <label className="form-label text-primary fw-medium">Empresa</label>
                                                                <Select
                                                                    value={selectedEmpresa}
                                                                    onChange={setSelectedEmpresa}
                                                                    styles={customStyles}
                                                                    options={dataEmpresas.map(empresa => ({
                                                                        value: empresa.id,
                                                                        label: empresa.titulo
                                                                    }))}
                                                                    placeholder="Selecione uma empresa"
                                                                    noOptionsMessage={() => "Nenhuma empresa encontrada"}
                                                                    className="react-select-container"
                                                                    classNamePrefix="react-select"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-center bg-light">
                        <button
                            type="submit"
                            className="btn btn-primary text-white px-5 py-2"
                            disabled={loadingSave}
                        >
                            {loadingSave ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Salvando...
                                </>
                            ) : (
                                'Salvar'
                            )}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary text-primary px-5 py-2 ms-4"
                            onClick={() => {
                                setShowModalEditarUnidade(false);
                                setSelectedEmpresa(null);
                            }}
                            disabled={loadingSave}
                        >
                            Cancelar
                        </button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* Modal Deletar Unidade */}
            <Modal size="lg" show={showModalDeletarUnidade.show} onHide={() => setShowModalDeletarUnidade({ show: false, id: null, nome: '' })}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmação</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Você tem certeza que deseja deletar a unidade <strong>"{showModalDeletarUnidade.nome}"</strong>?
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center bg-light">
                    <button 
                        onClick={() => handleDeletarUnidade(showModalDeletarUnidade.id)}
                        className="btn btn-primary text-white px-5 py-2"
                    >
                        Deletar
                    </button>
                    <button 
                        className="btn btn-outline-primary text-primary px-5 py-2 ms-4" 
                        onClick={() => setShowModalDeletarUnidade({ show: false, id: null, nome: '' })}
                    >
                        Cancelar
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default Unidades; 